'use client'
import { ClickToShowModal, ModalType } from '@/app/components/modal'
import AddIcon from '@/app/icons/add'
import LockIcon from '@/app/icons/lock'
import ShareIcon from '@/app/icons/share'
import Avatar from '@/components/avatar'
import Button from '@/components/button'
import Username from '@/components/username'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { NexaBlack } from '@/styles/fonts'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import Link from 'next/link'
import { ReactNode, useContext } from 'react'

type Props = { channel: IChannel; title: ReactNode }

const Breadcrumbs = ({ channel }: { channel: IChannel }) => {
    const crumbs = (channel.ancestors || []).map((ancestor, index) => {
        const name = ancestor.short_display_name || ancestor.display_name
        return (
            <div key={ancestor.id} className="flex items-center gap-2">
                {index !== 0 && <span>/</span>}
                <Link href={ancestor.url}>
                    <h5>{name}</h5>
                </Link>
            </div>
        )
    })
    return crumbs.length > 1 ? <div className="text-giphyLightGrey flex gap-2">{crumbs}</div> : null
}

const ChannelPageHeader = ({ channel, title }: Props) => (
    <>
        <Breadcrumbs channel={channel} />
        <div className="mr-2 flex items-center gap-3">
            <Avatar user={channel.user} className="object-cover md:hidden" />
            <div>
                <h2 className={`mb-1 flex text-2xl md:text-4xl ${NexaBlack.className}`}>
                    <div>{title}</div>
                    {channel.is_private && (
                        <div className="grid h-10 place-items-center px-2">
                            <LockIcon className="fill-giphyRed size-6" />
                        </div>
                    )}
                </h2>
                <Username user={channel.user} className="text-giphyBlue hover:underline" />
            </div>
        </div>
    </>
)

const actionButtonClassName = 'bg-gradient-grey hidden h-10 cursor-pointer md:block w-full'
export const DeactivateUserButton = ({ channel }: { channel: IChannel }) => {
    const { user } = useContext(UserContext)
    return user?.is_staff ? (
        <ClickToShowModal modal={{ name: 'deactivateUser', channel }}>
            <Button className={actionButtonClassName} style={{ width: 153 }}>
                Deactivate User
            </Button>
        </ClickToShowModal>
    ) : null
}

export const DerankUserButton = ({ channel }: { channel: IChannel }) => {
    const { user } = useContext(UserContext)
    return user?.is_staff ? (
        <ClickToShowModal modal={{ name: 'derankUser', channel }}>
            <Button className={actionButtonClassName} style={{ width: 153 }}>
                Derank User GIFs
            </Button>
        </ClickToShowModal>
    ) : null
}

export const ShareCollectionButton = ({ channel }: { channel: IChannel }) => {
    const { user } = useContext(UserContext)
    const { showMessage } = useContext(FlashMessageContext)
    const isOwnChannel = user && (user.is_staff || user.username === channel.user.username)
    const copy = channel.parent ? 'Collection' : 'Channel'
    return (
        !isOwnChannel && (
            <Button
                onClick={() => {
                    navigator?.clipboard.writeText(location.href)
                    showMessage({ message: `${copy} link copied to your clipboard`, type: 'success' })
                }}
                className="bg-gradient-grey flex h-10 shrink-0 items-center gap-2"
            >
                <ShareIcon className="fill-giphyWhite size-4" /> <div>Share {copy}</div>
            </Button>
        )
    )
}

export const EditCollectionButton = ({ channel }: { channel: IChannel }) => {
    const showEditCollection = useCanEdit(channel)
    const modal: ModalType = channel.parent
        ? { name: 'editCollection', channelId: channel.id, channel }
        : { name: 'editCollections', channelId: channel.id }
    const label = channel.parent ? 'Edit Collection' : 'Edit Collections'
    return showEditCollection ? (
        <ClickToShowModal modal={modal} className="hidden md:block">
            <Button className={actionButtonClassName}>{label}</Button>
        </ClickToShowModal>
    ) : null
}

export const NewCollectionButton = ({ channel }: { channel: IChannel }) => {
    const showEditCollection = useCanEdit(channel)
    return showEditCollection ? (
        <ClickToShowModal modal={{ name: 'createCollection', channelId: channel.id }} className="hidden md:block">
            <Button className={cn(actionButtonClassName, 'hidden items-center gap-2 md:flex')}>
                <AddIcon className="fill-giphyWhite size-4" /> <div>Create Collection</div>
            </Button>
        </ClickToShowModal>
    ) : null
}

export const AddGifsToCollectionButton = ({ channel }: { channel: IChannel }) => {
    const showEditCollection = useCanEdit(channel)
    return showEditCollection ? (
        <ClickToShowModal modal={{ name: 'addGifsToCollection', channelId: channel.id, channel }}>
            <Button className="bg-gradient-grey hidden h-10 cursor-pointer md:block" onClick={() => {}}>
                Add GIFs to Collection
            </Button>
        </ClickToShowModal>
    ) : null
}

function useCanEdit(channel: IChannel) {
    const { user } = useContext(UserContext)
    return user && (user.is_staff || user.username === channel.user.username)
}
export default ChannelPageHeader
